import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Monitor, Smartphone, X } from "lucide-react";
import ConfigAPI, { Config } from "../../../api/ConfigAPI";
import ComissionContractsAPI from "../../../api/ComissionContractsAPI";
import CONFIG from "../../../config/config";
import { CardV2 } from "../../../components/CardV2/CardV2";
import FileUploadWrapper from "../../../components/FileUploadWrapper/FileUploadWrapper";
import SectionTitle from "../../../components/SectionTitle/SectionTitle";
import { useImageUploader } from "../../../hooks/useImageUploader";
import { toastService } from "../../../services/ToastService";
import { cn } from "../../../lib/utils";

const COMMISSION_BACKGROUND_MAX_UPLOAD_MB = 2.5;

export default function AdvancedTabSection() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<Config | null>(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [backgroundScreen, setBackgroundScreen] = useState<"desktop" | "mobile">("desktop");
  const { uploadImage } = useImageUploader(undefined, (response) => response.data.url, "images");

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsConfigLoading(true);
        const res = await ConfigAPI.get();
        setConfig(res.data);
      } catch (error) {
        console.error("Failed to load configuration for commission background", { error });
      } finally {
        setIsConfigLoading(false);
      }
    };
    loadConfig();
  }, []);

  const desktopBgUrl = config?.commissionBackgroundImageUrl;
  const mobileBgUrl = config?.commissionBackgroundImageUrlMobile;

  const backgroundPreviewSrc = useMemo(() => {
    if (backgroundScreen === "desktop") {
      return desktopBgUrl;
    }
    return mobileBgUrl || desktopBgUrl;
  }, [backgroundScreen, desktopBgUrl, mobileBgUrl]);

  const handleBackgroundImageUpload = async (file: File) => {
    if (!file || !config) return;
    try {
      setIsConfigLoading(true);
      const fileUrl = await uploadImage(file);
      if (!fileUrl) {
        throw new Error("File upload failed");
      }
      const payload =
        backgroundScreen === "desktop"
          ? { commissionBackgroundImageUrl: fileUrl }
          : { commissionBackgroundImageUrlMobile: fileUrl };

      const response = await ComissionContractsAPI.updateCommissionBackground(payload);
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to upload commission background image", { backgroundScreen, error });
      toastService.showToast(t("toast.errors.logo") ?? "Failed to upload image", "error");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleRemoveBackgroundImage = async () => {
    if (!config) return;
    try {
      setIsConfigLoading(true);
      const response = await ComissionContractsAPI.deleteCommissionBackground();
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to remove commission background image", { error });
      toastService.showToast(t("toast.errors.logo") ?? "Failed to remove image", "error");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const screenToggle = (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-gray-800">
        {t("commission_settings_page.advanced.background.screen.title") || "Screen"}
      </p>
      <div className="inline-flex w-fit items-center gap-2 rounded-full bg-muted p-1">
        <button
          type="button"
          className={cn(
            "flex items-center gap-1 px-3 py-1 text-xs rounded-full transition-colors",
            backgroundScreen === "desktop"
              ? "bg-card shadow-sm text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
          disabled={isConfigLoading}
          onClick={() => setBackgroundScreen("desktop")}
        >
          <Monitor className="h-3 w-3" />
          <span>{t("commission_settings_page.advanced.background.screen.desktop") || "Desktop"}</span>
        </button>
        <button
          type="button"
          className={cn(
            "flex items-center gap-1 px-3 py-1 text-xs rounded-full transition-colors",
            backgroundScreen === "mobile"
              ? "bg-card shadow-sm text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
          disabled={isConfigLoading}
          onClick={() => setBackgroundScreen("mobile")}
        >
          <Smartphone className="h-3 w-3" />
          <span>{t("commission_settings_page.advanced.background.screen.mobile") || "Mobile"}</span>
        </button>
      </div>
      <p className="text-xs text-gray-600">
        {backgroundScreen === "desktop"
          ? t("commission_settings_page.advanced.background.editing.desktop") ||
            "You are editing the desktop version of the commission request page."
          : t("commission_settings_page.advanced.background.editing.mobile") ||
            "You are editing the mobile version of the commission request page."}
      </p>
    </div>
  );

  return (
    <CardV2>
      <div className="p-4 space-y-6">
        <p className="text-xs text-muted-foreground">
          {t("commission_settings_page.advanced.description") ||
            "Configure advanced options for your public commission request page, including the background image."}
        </p>

        <div className="space-y-4">
          <SectionTitle
            text={t("commission_settings_page.advanced.background.title") || "Background image"}
            clazz="text-base"
          />
          <p className="text-xs text-gray-600">
            {t("commission_settings_page.advanced.background.subtitle") ||
              "Upload an image to use as the background of your public commission request page."}
          </p>

          {(desktopBgUrl || mobileBgUrl) && (
            <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                {t("commission_settings_page.advanced.background.preview") || "Current background image:"}
              </p>
              {backgroundScreen === "mobile" && !mobileBgUrl && desktopBgUrl && (
                <p className="text-xs text-gray-500 mb-2">
                  {t("commission_settings_page.advanced.background.preview.mobile_fallback")}
                </p>
              )}
              {backgroundScreen === "desktop" && !desktopBgUrl && mobileBgUrl && (
                <p className="text-xs text-gray-500 mb-2">
                  {t("commission_settings_page.advanced.background.preview.desktop_missing")}
                </p>
              )}
              {backgroundPreviewSrc && (
                <div className="relative max-w-xl rounded-lg overflow-hidden border bg-gray-50">
                  <img
                    src={backgroundPreviewSrc}
                    alt="Commission background preview"
                    className="w-full h-48 object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveBackgroundImage}
                    className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-card/80 text-foreground hover:bg-card shadow-sm"
                    aria-label={
                      t("commission_settings_page.advanced.background.remove") || "Remove background image"
                    }
                    disabled={isConfigLoading}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
              {screenToggle}
            </div>
          )}

          {!(desktopBgUrl || mobileBgUrl) && screenToggle}

          <FileUploadWrapper
            label={
              backgroundScreen === "desktop"
                ? "commission_settings_page.advanced.background.upload"
                : "commission_settings_page.advanced.background.upload.mobile"
            }
            labelOptions={{ maxSize: COMMISSION_BACKGROUND_MAX_UPLOAD_MB }}
            onChange={(file) => {
              if (file) {
                handleBackgroundImageUpload(file as File);
              }
            }}
            allowedTypes={CONFIG.IMAGE_TYPES}
            isDisabled={isConfigLoading}
            maxSize={COMMISSION_BACKGROUND_MAX_UPLOAD_MB}
            showMaxSizeInSizeError
            allowedMultipleFiles={false}
          />
        </div>
      </div>
    </CardV2>
  );
}
